import React, { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUserById, updateUser, deleteUser } from '../../api/users';
import { Card, Typography, Button, Space, Descriptions, List, Tabs, Modal, message, Row, Col, Empty, Tag, Grid, Divider } from 'antd';
import { EditOutlined, DeleteOutlined, LockOutlined, PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import type { User, UserRole } from '../../types/users';
import type { Contact } from '../../types/common';
import UserModal from '../../components/features/users/UserModal';
import ContactEditModal from '../../components/modal/ContactEditModal';
import ChangePasswordModal from '../../components/features/users/ChangePasswordModal';
import ConfirmDeleteModal from '../../components/modal/ConfirmDeleteModal';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const UserDetailsPage: React.FC = () => {
  const { t } = useTranslation(['users', 'common']);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const stateUser = location.state?.user as User | undefined;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Contacts modal state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | undefined>(undefined);

  const { data: user, isLoading } = useQuery<User | null>({
    queryKey: ['user', id],
    queryFn: () => getUserById(id!),
    enabled: !stateUser && !!id,
    initialData: stateUser,
    staleTime: 5 * 60 * 1000,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<User>) => updateUser(id!, data),
    onSuccess: async () => {
      message.success('User updated successfully');
      // Refetch the latest user data after update
      await queryClient.invalidateQueries({ queryKey: ['user', id] });
      setIsModalOpen(false);
    },
    onError: () => message.error('Failed to update user'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteUser(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      navigate('/users');
    },
  });

  const handleEdit = () => {
    setIsModalOpen(true);
  };

  const handlePasswordChange = () => {
    setIsPasswordModalOpen(true);
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  // Contacts handlers
  const handleAddContact = () => {
    setEditingContact(undefined);
    setContactModalOpen(true);
  };
  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setContactModalOpen(true);
  };
  const handleDeleteContact = (contact: Contact) => {
    Modal.confirm({
      title: 'Delete Contact',
      content: 'Are you sure you want to delete this contact?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        const newContacts = (user?.contacts || []).filter(c => c !== contact);
        updateMutation.mutate({ contacts: newContacts });
      },
    });
  };
  const handleSaveContact = (contacts: Contact[]) => {
    // Only one contact is edited/added at a time
    let newContacts = user?.contacts ? [...user.contacts] : [];
    if (editingContact) {
      // Edit
      newContacts = newContacts.map(c => (c === editingContact ? contacts[0] : c));
    } else {
      // Add
      newContacts.push(contacts[0]);
    }
    updateMutation.mutate({ contacts: newContacts });
    setContactModalOpen(false);
    setEditingContact(undefined);
  };

  if (isLoading || !user) return <Card loading />;

  return (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col>
            <Space>
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/users')}
              >
                {t('common:back')}
              </Button>
              <Title level={3} style={{ margin: 0 }}>
                {user.displayName || user.email}
              </Title>
            </Space>
          </Col>
          <Col>
            <Space direction={isMobile ? "vertical" : "horizontal"} size="small">
              <Button 
                icon={<LockOutlined />} 
                onClick={handlePasswordChange}
                block={isMobile}
              >
                {t('users:changePassword')}
              </Button>
              <Button 
                icon={<EditOutlined />} 
                onClick={handleEdit}
                block={isMobile}
              >
                {t('common:edit')}
              </Button>
              <Button 
                danger 
                icon={<DeleteOutlined />} 
                onClick={handleDelete}
                block={isMobile}
              >
                {t('common:delete')}
              </Button>
            </Space>
          </Col>
        </Row>

        <Divider />

        <Tabs
          defaultActiveKey="details"
          items={[
            {
              key: 'details',
              label: 'Details',
              children: (
                <Descriptions bordered column={1} size="middle">
                  <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
                  <Descriptions.Item label="Display Name">{user.displayName}</Descriptions.Item>
                  <Descriptions.Item label="Status">{user.status}</Descriptions.Item>
                  <Descriptions.Item label="Roles">
                    <Space>
                      {user.roles.map((role: UserRole) => (
                        <Tag key={role} color="blue">{role}</Tag>
                      ))}
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Workspace">
                    {user.workspace?.id ? (
                      <a onClick={() => navigate(`/workspaces/${user.workspace?.id}`)}>
                        {user.workspace.name || user.workspace.id}
                      </a>
                    ) : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">{user.createdAt?.toString()}</Descriptions.Item>
                  <Descriptions.Item label="Last Login">{user.lastLogin?.toString() || '-'}</Descriptions.Item>
                  <Descriptions.Item label="ID">{user.id}</Descriptions.Item>
                </Descriptions>
              ),
            },
            {
              key: 'contacts',
              label: 'Contacts',
              children: (
                <Card
                  title={
                    <Row justify="space-between" align="middle">
                      <Col>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddContact}>
                          Add Contact
                        </Button>
                      </Col>
                    </Row>
                  }
                >
                  {(user.contacts && user.contacts.length > 0) ? (
                    <List
                      dataSource={user.contacts}
                      renderItem={(contact: Contact) => (
                        <List.Item
                          actions={[
                            <Button type="link" onClick={() => handleEditContact(contact)}>
                              Edit
                            </Button>,
                            <Button type="link" danger onClick={() => handleDeleteContact(contact)}>
                              Delete
                            </Button>,
                          ]}
                        >
                          <List.Item.Meta
                            title={contact.name}
                            description={
                              <Space direction="vertical" size={0}>
                                <Text type="secondary">Position: {contact.position}</Text>
                                <Text type="secondary">Email: {contact.email}</Text>
                                <Text type="secondary">Phone: {contact.phone}</Text>
                                <Text type="secondary">Type: {contact.type}</Text>
                              </Space>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  ) : (
                    <Empty description="No contacts yet" />
                  )}
                </Card>
              ),
            },
          ]}
        />
      </Space>

      <UserModal
        visible={isModalOpen}
        user={user}
        onCancel={() => setIsModalOpen(false)}
        onSuccess={() => setIsModalOpen(false)}
      />

      <ContactEditModal
        open={contactModalOpen}
        contact={editingContact}
        onCancel={() => {
          setContactModalOpen(false);
          setEditingContact(undefined);
        }}
        onSave={handleSaveContact}
        contactTypeOptions={[
          { value: 'Primary', label: 'Primary' },
          { value: 'Secondary', label: 'Secondary' },
          { value: 'Decision Maker', label: 'Decision Maker' },
        ]}
        t={(key: string) => key}
      />

      <ChangePasswordModal
        visible={isPasswordModalOpen}
        user={user}
        onCancel={() => setIsPasswordModalOpen(false)}
        onSuccess={() => setIsPasswordModalOpen(false)}
      />

      <ConfirmDeleteModal
        open={isDeleteModalOpen}
        title={t('users:deleteUser')}
        description={t('users:deleteUserConfirmation', { name: user.displayName || user.email })}
        onOk={() => {
          deleteMutation.mutate();
          setIsDeleteModalOpen(false);
        }}
        onCancel={() => setIsDeleteModalOpen(false)}
      />
    </Card>
  );
};

export default UserDetailsPage; 